import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { MovingBorderWrapper } from "@/components/ui/moving-border";
import { SEO, seoConfig } from "@/hooks/useSEO";
import { generateWebPageSchema, generateBreadcrumbSchema } from "@/lib/jsonld";

// Images
import heroImage from "@/assets/usecases/coachs/hero.webp";
import feature1Image from "@/assets/usecases/coachs/feature-1.webp";
import feature2Image from "@/assets/usecases/coachs/feature-2.webp";
import feature3Image from "@/assets/usecases/coachs/feature-3.webp";

const features = [
  {
    number: "1",
    title: "Garantissez un taux de présence élevé à vos lives",
    text: "Envoyez \"Live dans 5 minutes\" directement sur l'écran verrouillé de tous vos membres. Mettez le lien de connexion sur la carte pour éliminer définitivement l'excuse \"je ne trouvais pas le lien\". Résultat : taux de présence maximisé.",
    image: feature1Image,
    imagePosition: "left" as const,
  },
  {
    number: "2",
    title: "Mettez en avant vos contenus les plus importants",
    text: "Vous sortez un nouveau module de formation, une ressource bonus ou une mise à jour cruciale ? Ne laissez pas ça se noyer dans le flux. Une notification garantit que tous vos membres savent qu'un nouveau contenu les attend dès qu'il est disponible.",
    image: feature2Image,
    imagePosition: "right" as const,
  },
  {
    number: "3",
    title: "Cultivez une culture communautaire vivante",
    text: "Utilisez votre canal pour célébrer les victoires de vos membres, annoncer des sessions FAQ live, ou partager un message de motivation. L'outil parfait pour garder votre communauté connectée et engagée.",
    image: feature3Image,
    imagePosition: "left" as const,
  },
];

const badges = [
  "Vos prochains lives",
  "Vos nouveaux modules",
  "Vos annonces urgentes",
];

export default function UseCasesCoachs() {
  const jsonLdSchemas = [
    generateWebPageSchema("Coachs & Communautés", seoConfig.useCasesCoachs.description, "/cas-utilisation/coachs-communautes"),
    generateBreadcrumbSchema([
      { name: "Accueil", path: "/" },
      { name: "Cas d'utilisation", path: "/#a-qui-sadresse" },
      { name: "Coachs & Communautés", path: "/cas-utilisation/coachs-communautes" }
    ])
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <SEO {...seoConfig.useCasesCoachs} jsonLd={jsonLdSchemas} />
      <Header />

      {/* SECTION 1 - HERO */}
      <section className="relative pt-24 lg:pt-40 pb-[37.5px] sm:pb-[47.5px] lg:pb-[65px] overflow-hidden">
        <div className="container mx-auto px-6 lg:px-12 xl:px-20 relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 xl:gap-16 items-center">
            {/* Left: Text content */}
            <div className="max-w-2xl text-center lg:text-left mx-auto lg:mx-0">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-foreground mb-6">
                Le système d'alertes VIP pour <span className="text-[#074155]">vos</span> <span className="text-[#0c97a9]">communautés en ligne</span>
              </h1>

              <p className="leading-relaxed mb-8 font-medium text-[#4f5b5d] text-base sm:text-lg">
                Vous animez une communauté en ligne et vos annonces importantes se perdent dans les fils d'actualité et les boîtes mail ? <span className="font-semibold text-[#0c97a9]">Unotif</span> garantit que vos lives, nouveaux contenus et mises à jour arrivent directement sur l'écran verrouillé de tous vos membres.
              </p>

              <div className="flex flex-col items-center lg:items-start">
                <MovingBorderWrapper duration={3000} borderRadius="0.5rem">
                  <Button asChild size="lg" className="bg-primary hover:bg-[hsl(187,87%,32%)] text-primary-foreground shadow-lg hover:shadow-xl transition-all duration-300 text-base px-8 py-6">
                    <a href="https://app.unotif.com/auth?mode=signup">
                      Créez votre première carte
                    </a>
                  </Button>
                </MovingBorderWrapper>
                <p className="text-sm text-muted-foreground mt-2">
                  👉 Sans engagement, sans carte bancaire
                </p>
              </div>
            </div>

            {/* Right: Hero Image */}
            <div className="hidden lg:flex justify-center lg:justify-end items-center">
              <img
                src={heroImage}
                alt="Coachs et communautés avec Unotif"
                loading="lazy"
                className="rounded-2xl max-w-full h-auto"
                style={{
                  boxShadow: "0 8px 32px -8px rgba(0,0,0,0.12), 0 4px 16px -4px rgba(0,0,0,0.08)",
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2 - Transformez votre communauté en véritable canal de vente direct */}
      <section className="bg-[#FAFBFB] pt-[37.5px] sm:pt-[47.5px] lg:pt-[65px] pb-[37.5px] sm:pb-[47.5px] lg:pb-[65px]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#074155] mb-6">
              Transformez votre communauté en véritable <span className="text-[#0c97a9]">canal de vente direct</span>
            </h2>
            <p className="text-base sm:text-lg font-medium leading-relaxed text-[#4f5b5d]">
              <span className="font-semibold text-[#0c97a9]">Unotif</span> ne remplace pas votre plateforme communautaire. Il garantit que vos annonces cruciales ne passent plus inaperçues. La clé : offrir à vos membres une expérience VIP qui justifie leur abonnement.
            </p>
          </div>

          {/* Features */}
          <div className="space-y-12 sm:space-y-16 lg:space-y-20">
            {features.map((feature, index) => (
              <div
                key={index}
                className={`grid lg:grid-cols-2 gap-8 lg:gap-12 items-center ${
                  feature.imagePosition === "right" ? "lg:grid-flow-dense" : ""
                }`}
              >
                {/* Image */}
                <div className={feature.imagePosition === "right" ? "lg:col-start-2" : ""}>
                  <img
                    src={feature.image}
                    alt={feature.title}
                    loading="lazy"
                    className="rounded-2xl w-full h-auto"
                    style={{
                      boxShadow: "0 8px 32px -8px rgba(0,0,0,0.12), 0 4px 16px -4px rgba(0,0,0,0.08)",
                    }}
                  />
                </div>

                {/* Text */}
                <div className={feature.imagePosition === "right" ? "lg:col-start-1 lg:row-start-1" : ""}>
                  <div className="flex items-start gap-4">
                    <span className="text-4xl sm:text-5xl font-bold text-[#0c97a9]">
                      {feature.number}.
                    </span>
                    <div>
                      <h3 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4">
                        {feature.title}
                      </h3>
                      <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456]">
                        {feature.text}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Result text */}
          <div className="text-center mt-12 sm:mt-16 lg:mt-20 max-w-3xl mx-auto">
            <p className="text-base sm:text-lg font-semibold leading-relaxed text-[#074155]">
              Résultat : des membres qui ne ratent rien, qui se sentent valorisés, et qui restent engagés.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 3 - Vos membres paient pour de la valeur */}
      <section className="pt-[37.5px] sm:pt-[47.5px] lg:pt-[65px] pb-16 sm:pb-20 lg:pb-24">
        <div className="container mx-auto px-6 lg:px-12 xl:px-20">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#074155] mb-6">
              Vos membres paient pour de la valeur. <span className="text-[#0c97a9]">Assurez-vous qu'ils la voient.</span>
            </h2>
            <p className="text-base sm:text-lg font-medium leading-relaxed text-[#4f5b5d] mb-4">
              Vous avez le choix. Continuer à poster des annonces que la moitié de vos membres ne verra jamais.
            </p>
            <p className="text-base sm:text-lg font-medium leading-relaxed text-[#4f5b5d] mb-10">
              Ou garantir que chaque live, chaque nouveau contenu, chaque moment important touche vraiment toute votre communauté.
            </p>

            {/* Badges */}
            <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mb-6">
              {badges.map((badge, index) => (
                <span
                  key={index}
                  className="rounded-full border border-[#074155] bg-white px-4 py-2 text-sm sm:text-base font-medium text-[#074155]"
                >
                  {badge}
                </span>
              ))}
            </div>

            {/* Callout */}
            <p className="text-base sm:text-lg font-medium text-[#074155]">
              👀 Garantissez qu'ils ne ratent rien.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 4 - CTA Final */}
      <section className="pb-16 sm:pb-20 lg:pb-24">
        <div className="container mx-auto px-6 lg:px-12 xl:px-20">
          <Card className="max-w-2xl mx-auto text-center p-8 sm:p-10 lg:p-12 border border-gray-200 shadow-lg">
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-[#074155] mb-4">
              Votre canal communautaire en 5 minutes !
            </h2>
            <p className="text-base sm:text-lg font-medium text-[#4f5b5d] mb-8">
              Testez Unotif gratuitement pour votre communauté.
            </p>
            <div className="flex flex-col items-center">
              <Button asChild size="lg" className="bg-primary hover:bg-[hsl(187,87%,32%)] text-primary-foreground shadow-lg hover:shadow-xl transition-all duration-300 text-base px-8 py-6">
                <a href="https://app.unotif.com/auth?mode=signup">
                  Commencer maintenant
                </a>
              </Button>
              <p className="text-sm text-muted-foreground mt-3">
                Sans carte bancaire
              </p>
            </div>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
}
